import React, { useState } from 'react';

const STATUS_ORDER = ['To Apply', 'Applied', 'Interview', 'Offer', 'Rejected'];
const STATUS_COLOR = {
  'To Apply':  '#3a4460',
  'Applied':   '#2196f3',
  'Interview': '#ff9800',
  'Offer':     '#00e896',
  'Rejected':  '#f44336',
};

const TIER_COLOR = {
  high:    '#00e896',
  medium:  '#f5c518',
  stretch: '#ff7043',
};

const POSTED_BADGE = {
  1: { label: '🔥 1 day ago',  bg: '#2d0e00', color: '#ff5c00', border: '#ff5c00' },
  2: { label: '⚡ 2 days ago', bg: '#001830', color: '#00aaff', border: '#00aaff' },
};

export default function JobCard({ job, accent, status, onStatusChange, animDelay }) {
  const [expanded, setExpanded] = useState(false);

  const tierColor  = TIER_COLOR[job.tier] || '#00e896';
  const posted     = POSTED_BADGE[job.daysAgo] || POSTED_BADGE[2];
  const statusNext = () => {
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(status) + 1) % STATUS_ORDER.length];
    onStatusChange(next);
  };

  return (
    <div
      className="fade-up"
      style={{
        ...styles.card,
        borderColor: expanded ? accent + '44' : '#1a1e35',
        animationDelay: `${animDelay}s`,
      }}
    >
      {/* Card Header – always visible */}
      <div style={styles.cardTop} onClick={() => setExpanded(!expanded)}>

        {/* Score */}
        <div style={{ ...styles.score, borderColor: tierColor, color: tierColor }}>
          {job.fitScore}
        </div>

        {/* Info */}
        <div style={styles.info}>
          <div style={styles.infoRow}>
            <span style={styles.company}>{job.company}</span>
            <span style={{
              ...styles.postedBadge,
              background: posted.bg, color: posted.color, border: `1px solid ${posted.border}`,
            }}>
              {posted.label}
            </span>
          </div>
          <div style={styles.role}>{job.role}</div>
          <div style={styles.meta}>
            📍 {job.location}
            {job.salary && <span> &nbsp;·&nbsp; 💰 {job.salary}</span>}
          </div>
        </div>

        {/* Right side */}
        <div style={styles.right}>
          <button
            style={{ ...styles.statusBtn, background: STATUS_COLOR[status] + '22', color: STATUS_COLOR[status] }}
            onClick={e => { e.stopPropagation(); statusNext(); }}
          >
            {status}
          </button>
          <span style={styles.chevron}>{expanded ? '▲' : '▼'}</span>
        </div>

      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={styles.expanded}>

          {/* Stack tags */}
          <div style={styles.tagRow}>
            {(job.stack || []).map(t => (
              <span key={t} style={styles.tag}>{t}</span>
            ))}
            <span style={{ ...styles.tierTag, color: tierColor, borderColor: tierColor + '44', background: tierColor + '11' }}>
              {job.tier?.toUpperCase()} FIT
            </span>
          </div>

          {/* Highlight */}
          {job.highlight && (
            <div style={styles.highlight}>
              🏢 {job.highlight}
            </div>
          )}

          {/* Why You Fit */}
          <div style={styles.whyBox}>
            <span style={{ color: accent, fontWeight: 700, fontSize: 11 }}>WHY YOU FIT &nbsp;</span>
            <span style={styles.whyText}>{job.whyYouFit}</span>
          </div>

          {/* Apply Button */}
          <div style={styles.actionRow}>
            <a
              href={job.applyUrl || '#'}
              target="_blank"
              rel="noreferrer"
              style={{ ...styles.applyBtn, background: accent, color: '#000' }}
            >
              Apply Now →
            </a>
            <span style={styles.typeLabel}>{job.type}</span>
          </div>

        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    background: '#0d0f1c',
    border: '1px solid',
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  cardTop: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 18px', cursor: 'pointer',
  },
  score: {
    minWidth: 48, height: 48, borderRadius: '50%',
    border: '2px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 800, flexShrink: 0,
  },
  info: { flex: 1, minWidth: 0 },
  infoRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 },
  company: { color: '#fff', fontWeight: 700, fontSize: 15 },
  postedBadge: {
    fontSize: 9, fontWeight: 700, letterSpacing: 1,
    padding: '2px 8px', borderRadius: 3,
  },
  role: { color: '#8a9ab8', fontSize: 12, marginBottom: 2 },
  meta: { color: '#3a4460', fontSize: 11 },
  right: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: 6, flexShrink: 0,
  },
  statusBtn: {
    border: 'none', padding: '4px 10px', borderRadius: 10,
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    letterSpacing: 0.5, whiteSpace: 'nowrap',
  },
  chevron: { color: '#3a4460', fontSize: 10 },

  expanded: {
    borderTop: '1px solid #1a1e35',
    padding: '14px 18px',
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  tagRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tag: {
    fontSize: 11, background: '#131628',
    border: '1px solid #1e2340', color: '#7a8ab0',
    padding: '3px 9px', borderRadius: 4,
  },
  tierTag: {
    fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
    border: '1px solid', padding: '3px 9px', borderRadius: 4,
  },
  highlight: {
    fontSize: 12, color: '#5a6a90',
    background: '#111326', border: '1px solid #1a1e35',
    padding: '8px 12px', borderRadius: 6,
  },
  whyBox: {
    fontSize: 12, lineHeight: 1.7,
    background: '#0a0d1a', border: '1px solid #141828',
    padding: '10px 14px', borderRadius: 8,
  },
  whyText: { color: '#9ab0d0' },
  actionRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 12,
  },
  applyBtn: {
    display: 'inline-block', padding: '9px 22px',
    borderRadius: 7, fontSize: 12, fontWeight: 800,
    textDecoration: 'none', letterSpacing: 0.5,
    transition: 'opacity 0.2s',
  },
  typeLabel: { color: '#3a4460', fontSize: 11 },
};
